import { addDays } from 'date-fns';
import * as React from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { request } from '../../../../utilities/widget-helpers/request';
import { toUtc } from '../../../../utilities/helper-fuctions';
import { ToastMessages } from '../../../notifications/toast-messages';
import { Form } from '../../../ui-components/form/form';
import { FormButtons } from '../../../ui-components/hoverable-buttons/form-modal-buttons/form-buttons';
import { DateTimePicker } from '../../../ui-components/inputs/date-picker/date-time-picker';
import { formatOptions } from '../../../ui-components/inputs/select/formatter';
import { Select } from '../../../ui-components/inputs/select/select';
import { Spinner } from '../../../ui-components/spinner/spinner';
import { WidgetModal } from '../../widget-modal';
import {
	getAirportRefuelEuByAid,
	getAirportRefuelEuByAirportAid,
	refuelEuEligibilityOptions,
} from './refuel-eu-helpers';
import './refuel-eu.scss';

const mapDispatchProps = (dispatch: any) => ({});

class RefuelEuFormComponent extends WidgetModal<any, any> {
	public form: any = new Form({});

	public state: any = {
		...this.state,
		loading: true,
		error: false,
		isEdit: false,
		entityAid: null,
		requiresDates: false,
		errorInDate: false,
		formReady: false,
		datePickerSeed: Date.now(),
	};

	public async componentDidMount() {
		super.componentDidMount();
		await this.loadEntity();
	}

	public componentDidUpdate(
		prevProps: Readonly<any>,
		prevState: Readonly<any>
	) {
		if (
			this.state?.requiresDates &&
			(!prevState?.requiresDates ||
				prevState?.datePickerSeed !== this.state?.datePickerSeed)
		) {
			this.ensureRequiredDates();
		}
	}

	public getTitle() {
		return this.state?.isEdit ? 'Edit REFUELEU' : 'Create REFUELEU';
	}

	public getType() {
		return 'refuel eu';
	}

	public renderContent(): any {
		if (this.state.error) {
			return this.renderCustomError();
		}

		if (this.state.loading || !this.state.formReady) {
			return (
				<div className='display-flex align-items-center justify-content-center p-6'>
					<Spinner size='large' />
				</div>
			);
		}

		return this.renderForm();
	}

	public renderFooter(): any {
		return (
			<FormButtons
				isDelete={false}
				onClose={() => this.onClose()}
				onSave={() => this.onSave()}
				saveDisabled={
					this.state?.buttonDisabled ||
					this.state?.loading ||
					!!this.state?.error
				}
				loading={this.state?.saveLoading}
				type={this.getType()}
			/>
		);
	}

	public renderForm(): React.ReactElement {
		const validFrom = this.form.getValue('validFrom');
		const validTo = this.form.getValue('validTo');

		return (
			<form className='RefuelEuModalBody p-4'>
				<Select
					classes='py-2'
					field={this.form.getField('isEligible')}
					labelInfos={{ label: 'REFUELEU eligible' }}
					options={formatOptions(refuelEuEligibilityOptions, false)}
				/>

				{this.state?.requiresDates && (
					<div className='FormContainer'>
						<DateTimePicker
							key={`refuel-valid-from-${this.state.datePickerSeed}-${
								validFrom ? 'set' : 'empty'
							}`}
							field={this.form.getField('validFrom')}
							inputClassName='palette--c-neutral-5'
							classes='py-2'
							days={0}
							showTimeInput={true}
							useHour={true}
							shouldCloseOnSelect={false}
							isIcon={true}
							labelInfos={{ label: 'Valid from' }}
						/>
						<DateTimePicker
							key={`refuel-valid-to-${this.state.datePickerSeed}-${
								validTo ? 'set' : 'empty'
							}`}
							field={this.form.getField('validTo')}
							inputClassName='palette--c-neutral-5'
							classes='py-2 left-m'
							days={7}
							showTimeInput={true}
							useHour={true}
							shouldCloseOnSelect={false}
							isIcon={true}
							labelInfos={{ label: 'Valid to' }}
						/>
					</div>
				)}

				{!this.state?.requiresDates && (
					<p className='palette--c-neutral-5 mb-0 mt-2'>
						Date fields are only needed when eligibility is set to{' '}
						<span className='fw-bold'>Yes</span>.
					</p>
				)}

			</form>
		);
	}

	public onClose(isDelete = false): void {
		this.modal$?.close();
		this.form?.clearForm();
		this.props?.onClose(isDelete);
	}

	public async onSave() {
		this.setState({ buttonDisabled: true, saveLoading: true });

		const values = this.generateValues();
		const changes = this.form.getDifferences();

		const req = request(
			'/airportrefueleus/merge',
			'refuel-eu',
			() => this.handleSuccessModal(),
			(err) =>
				toast.error(<ToastMessages error={err} />, { theme: 'light' })
		);

		if (this.state?.isEdit && this.state?.entityAid) {
			await req.edit(
				`/airportrefueleus/${this.state.entityAid}`,
				values,
				changes
			);
		} else {
			await req.create('/airportrefueleus', values, changes, {
				parentAid: this.props?.airportAid || this.props?.aid,
			});
		}

		this.setState({ buttonDisabled: false, saveLoading: false });
	}

	public async onDelete(): Promise<void> {
		if (!this.state?.entityAid) {
			return;
		}

		this.setState({ buttonDisabled: true, saveLoading: true });

		request(
			'/airportrefueleus/merge',
			'refuel-eu-delete',
			() => this.handleSuccessModal(),
			(err) =>
				toast.error(<ToastMessages error={err} />, { theme: 'light' })
		).deleteF(
			`/airportrefueleus/${this.state.entityAid}`,
			this.form?.getFieldsWhereHasValue()
		);

		this.setState({ buttonDisabled: false, saveLoading: false });
	}

	public handleSuccessModal() {
		if (this.notif$) {
			this.notif$.open();
		}
	}

	private renderCustomError() {
		return (
			<div className='p-4'>
				<p className='palette--c-red-2 m-0'>
					There was an error loading REFUELEU data.
				</p>
			</div>
		);
	}

	private async loadEntity() {
		try {
			this.setState({ loading: true, error: false });

			const entity = this.props?.isEdit
				? await getAirportRefuelEuByAid(this.props?.aid)
				: await getAirportRefuelEuByAirportAid(
						this.props?.airportAid || this.props?.aid
				  );

			this.initializeForm(entity);
		} catch (err) {
			this.setState({ error: err, loading: false, formReady: true });
		}
	}

	private initializeForm(entity) {
		const isEligible = entity?.isEligible || '';
		const requiresDates = isEligible === 'YES';

		this.form = new Form(
			{
				isEligible: {
					value: isEligible,
					mandatory: true,
					trigger: () => this.handleEligibilityChange(),
				},
				validFrom: {
					value: entity?.validFrom ? new Date(entity?.validFrom) : '',
					mandatory: requiresDates,
					trigger: () => this.handleDateChange(),
				},
				validTo: {
					value: entity?.validTo ? new Date(entity?.validTo) : '',
					mandatory: requiresDates,
					trigger: () => this.handleDateChange(),
				},
			},
			(changed) => this.handleFormChange(changed)
		);

		this.setState(
			{
				loading: false,
				error: false,
				isEdit: !!entity?.aid,
				entityAid: entity?.aid || null,
				requiresDates,
				formReady: true,
				buttonDisabled: true,
			},
			() => {
				if (requiresDates) {
					this.ensureRequiredDates();
				}
				this.reEvaluate();
			}
		);
	}

	private handleEligibilityChange() {
		const isEligible = this.form.getValue('isEligible');
		const requiresDates = isEligible === 'YES';

		this.form.getField('validFrom').mandatory = requiresDates;
		this.form.getField('validTo').mandatory = requiresDates;

		if (!requiresDates) {
			this.form.setValue('validFrom', null);
			this.form.setValue('validTo', null);
		}

		this.setState(
			{
				requiresDates,
				errorInDate: false,
				datePickerSeed: Date.now(),
			},
			() => {
				if (requiresDates) {
					this.ensureRequiredDates();
				}
				this.reEvaluate();
			}
		);
	}

	private ensureRequiredDates() {
		const now = new Date();
		const validFrom = this.form.getValue('validFrom');
		const validTo = this.form.getValue('validTo');
		const fromDate = validFrom ? new Date(validFrom) : null;
		const toDate = validTo ? new Date(validTo) : null;
		const invalidFrom =
			!fromDate || Number.isNaN(fromDate.getTime()) || fromDate.getTime() <= 0;
		const invalidTo =
			!toDate || Number.isNaN(toDate.getTime()) || toDate.getTime() <= 0;

		if (invalidFrom) {
			this.form.getField('validFrom').newValue = now;
		}

		if (invalidTo) {
			this.form.getField('validTo').newValue = addDays(now, 7);
		}
	}

	private handleDateChange() {
		const validFrom = this.form.getValue('validFrom');
		const validTo = this.form.getValue('validTo');
		const hasError =
			this.state?.requiresDates &&
			!!validFrom &&
			!!validTo &&
			new Date(validFrom) > new Date(validTo);

		this.setState({ errorInDate: hasError }, () => this.reEvaluate());
	}

	private handleFormChange(changed) {
		if (!this.state?.formReady) {
			return;
		}

		this.setState({
			buttonDisabled: !changed || this.state?.errorInDate,
		});
	}

	private reEvaluate() {
		if (!this.form) {
			return;
		}

		const changed = this.form.hasChanged();
		this.setState({
			buttonDisabled: !changed || this.state?.errorInDate,
		});
	}

	private generateValues() {
		const values: any = this.form.generateJSON();

		if (values?.isEligible === 'YES') {
			values.validFrom = values?.validFrom
				? toUtc(values.validFrom)
				: null;
			values.validTo = values?.validTo ? toUtc(values.validTo) : null;
		} else {
			values.validFrom = null;
			values.validTo = null;
		}

		return values;
	}
}

export const RefuelEuForm: any = connect(
	null,
	mapDispatchProps,
	null,
	{ forwardRef: true }
)(RefuelEuFormComponent);
